import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';

const PageEditor = ({ pageType }) => {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const pageTitles = {
    terms: 'Terms of Service',
    privacy: 'Privacy Policy',
    faq: 'FAQ'
  };

  const defaultContent = useMemo(() => ({
    terms: `Terms of Service

Welcome to OLYMP. By accessing our website and using our services, you agree to these terms of service.

1. Services
We provide Discord-related services including but not limited to server boosts, members, and reactions.

2. Payments
All payments are processed securely. Once payment is confirmed, services are delivered automatically.

3. Refunds
Refund requests are handled on a case-by-case basis. Contact our support team for assistance.`,
    privacy: `Privacy Policy

Your privacy is important to us. This policy outlines how we collect and use your information.

1. Information Collection
We collect only necessary information required to provide our services.

2. Data Usage
Your data is used solely for service delivery and communication purposes.

3. Security
We implement security measures to protect your information.`,
    faq: `Frequently Asked Questions

How do I place an order?
Select your desired service, choose the quantity, and proceed to checkout.

How long does delivery take?
Most services are delivered instantly after payment confirmation.

How can I contact support?
You can reach us through our Discord server or Telegram channel.`
  }), []); // L'objet ne changera jamais, donc pas de dépendances

  useEffect(() => {
    const fetchContent = async () => {
      try {
        // D'abord, essayons de charger depuis l'endpoint public
        const publicResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/pages/${pageType}`);
        
        if (publicResponse.ok) {
          const data = await publicResponse.json();
          setContent(data.content || defaultContent[pageType]);
        } else {
          // Si l'endpoint public échoue, on utilise le contenu par défaut
          console.log(`No existing ${pageType} content found, using default`);
          setContent(defaultContent[pageType]);
        }
      } catch (error) {
        console.error(`Error fetching ${pageType}:`, error);
        setContent(defaultContent[pageType]);
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, [pageType, defaultContent]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        throw new Error('No admin token found');
      }

      console.log(`Saving ${pageType} content...`);

      // Première tentative avec PATCH
      let response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/content/${pageType}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ content })
      });

      // Si PATCH ne fonctionne pas, essayons PUT
      if (response.status === 405) {
        console.log('PATCH not allowed, trying PUT...');
        response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/content/${pageType}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ content })
        });
      }

      // Si PUT ne fonctionne pas non plus, essayons POST
      if (response.status === 405) {
        console.log('PUT not allowed, trying POST...');
        response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/content/${pageType}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ content })
        });
      }

      let responseData;
      const responseText = await response.text();
      try {
        responseData = JSON.parse(responseText);
      } catch (e) {
        responseData = responseText;
      }

      console.log('Server response:', responseData);

      if (!response.ok) {
        throw new Error(typeof responseData === 'string' ? responseData : JSON.stringify(responseData));
      }

      toast.success(`${pageTitles[pageType]} updated successfully`);
    } catch (error) {
      console.error(`Error updating ${pageType}:`, error);
      toast.error(error.message || `Failed to update ${pageTitles[pageType]}`);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    setContent(e.target.value);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#22c55e]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">{pageTitles[pageType]}</h2>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2 bg-[#22c55e] text-white rounded-lg hover:bg-[#1ea34b] transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
      <div className="space-y-4">
        <textarea
          value={content}
          onChange={handleChange}
          spellCheck="false"
          className="w-full h-[60vh] px-4 py-3 bg-[#1a1a1a] text-white border border-gray-700 rounded-lg focus:border-[#22c55e] focus:ring-1 focus:ring-[#22c55e] transition-colors duration-200 font-mono resize-none"
          placeholder={`Enter ${pageTitles[pageType]} content here...`}
        />
        <p className="text-sm text-gray-400">
          Use plain text to write your content. Line breaks and spacing will be preserved.
        </p>
      </div>
    </div>
  );
};

export default PageEditor; 